import { useEffect, useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Button } from "./Button";

export type DrawerProps = {
  open: boolean;
  title: string;
  side?: "left" | "right";
  role?: "dialog" | "complementary" | "region";
  onClose?: (() => void) | null;
  closeLabel: string;
  pinned?: boolean | null;
  onPin?: ((pinned: boolean) => void) | null;
  pinLabel?: string;
  unpinLabel?: string;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  dataFxmap?: string;
  width?: string;
  autoFocus?: boolean;
};

export function Drawer({ open, title, side = "left", role = "dialog", onClose = null, closeLabel, pinned = null, onPin = null, pinLabel = "", unpinLabel = "", header, footer, children, className, dataFxmap, width, autoFocus = false }: DrawerProps) {
  const titleId = useId();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open || !autoFocus) return;

    ref.current?.focus();
  }, [open, autoFocus]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape" || onClose === null) return;

    event.stopPropagation();
    onClose();
  };

  return (
    <aside
      ref={ref}
      className={["fxmap-drawer", `fxmap-drawer-${side}`, pinned ? "fxmap-drawer-pinned" : "", className ?? ""].filter(Boolean).join(" ")}
      role={role}
      aria-labelledby={titleId}
      aria-modal={role === "dialog" ? false : undefined}
      data-fxmap={dataFxmap ?? "drawer"}
      style={width !== undefined ? { width } : undefined}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <header className="fxmap-drawer-header">
        <h2 id={titleId} className="fxmap-drawer-title fxmap-display fxmap-truncate">
          {title}
        </h2>
        <div className="fxmap-drawer-header-tools">
          {header}
          {onPin !== null && pinned !== null ? <Button kind="icon" size="sm" icon={pinned ? "pinned" : "pin"} label={pinned ? unpinLabel : pinLabel} pressed={pinned} onClick={() => onPin(!pinned)} /> : null}
          {onClose !== null ? <Button kind="icon" size="sm" icon="close" label={closeLabel} onClick={onClose} data-fxmap-action="close" /> : null}
        </div>
      </header>
      <div className="fxmap-drawer-body">{children}</div>
      {footer !== undefined ? <footer className="fxmap-drawer-footer">{footer}</footer> : null}
    </aside>
  );
}
